<template>
  <div class="list-clients">
    <BContainer fluid class="px-1 px-md-3">
      <BRow align-h="end" class="no-gutters pt-4 pb-5">
        <BCol cols="12" md="7" class="d-none d-md-block">
          {{ disclaimer }}
        </BCol>
        <BCol cols="12" md="1" v-if="countries.length" class="text-right d-none d-md-block">
          <div class="list-clients__filters__title text-muted text-uppercase mr-3 d-inline-block">
            {{ $t('list.filterBy') }}
          </div>
        </BCol>
        <BCol cols="6" md="2">
          <BDropdown class="list-clients__filters__dropdown w-100 mr-1" :class="{ 'list-clients__filters__dropdown--default': selectedCountry === $t('list.country') }">
            <template #button-content>
              <div class="d-flex align-items-center ml-1 my-2 my-md-0">
                <div class="list-clients__filters__dropdown__text text-truncate flex-grow-1 text-uppercase">
                  {{ selectedCountry }}
                </div>
                <FontAwesomeIcon icon="chevron-down" size="xs" class=""></FontAwesomeIcon>
              </div>
            </template>
            <BDropdownItem v-for="country in countries" :key="country.iso" @click="selectCountry(country.iso)">
              {{ country.label }}
            </BDropdownItem>
          </BDropdown>
        </BCol>
        <BCol cols="6" md="2">
          <BDropdown class="list-clients__filters__dropdown w-100" :class="{ 'list-clients__filters__dropdown--default': selectedBank === $t('list.banksFilingSars') }" right>
            <template #button-content>
              <div class="d-flex align-items-center ml-1 my-2 my-md-0">
                <div class="list-clients__filters__dropdown__text text-truncate flex-grow-1 text-uppercase">
                  {{ selectedBank }}
                </div>
                <FontAwesomeIcon icon="chevron-down" size="xs" class=""></FontAwesomeIcon>
              </div>
            </template>
            <BDropdownItem v-for="bank in banks" :key="bank" @click="selectBank(bank)">
              {{ bank }}
            </BDropdownItem>
          </BDropdown>
        </BCol>
      </BRow>
      <BRow class="text-center no-gutters mx-md-n3" v-if="clients.length > 0">
        <BCol cols="6" md="3" v-for="client in clients" :key="client.id" class="list-clients__client mb-5 px-1 px-md-3">
          <RouterLink :to="{ name: 'client.profile', params: { id: kebabCase(client.long_name) } }">
            <div class="list-clients__client__image">
              <img :src="getClientImage(client)" class="bg-white w-100">
            </div>
            <FontAwesomeIcon icon="search-plus" size="2x" class="list-clients__client__icon d-none d-md-block"></FontAwesomeIcon>
            <div class="text-left">
              <div class="font-weight-bold">
                {{ client.short_name }}
              </div>
              <div class="font-size-sm">
                {{ getClientCountries(client) }}
              </div>
              <div>
                {{ client.category }}
              </div>
              <div>
                {{ client.bank_filing_sars___gallery }}
              </div>
            </div>
          </RouterLink>
        </BCol>
      </BRow>
      <BRow v-else>
        <BCol class="mb-3 font-italic text-center">
          {{ $t('list.noClients') }}
        </BCol>
      </BRow>
    </BContainer>
  </div>
</template>

<script>
import { compact, flatten, get, kebabCase, map, sortBy, sortedUniq, split, trim } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import utils from '@/mixins/utils'

export default {
  name: 'List',
  components: {
    FontAwesomeIcon
  },
  mixins: [utils],
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.$store.commit('clients/filterByBank', get(to.query, 'bank', null))
      vm.$store.commit('clients/filterByCategory', get(to.query, 'category', null))
      vm.$store.commit('clients/filterByCountry', get(to.query, 'country', null))
      document.getElementById('app__container').scrollTop = 0
    })
  },
  beforeRouteUpdate (to, from, next) {
    this.$store.commit('clients/filterByBank', get(to.query, 'bank', null))
    this.$store.commit('clients/filterByCategory', get(to.query, 'category', null))
    this.$store.commit('clients/filterByCountry', get(to.query, 'country', null))
    document.getElementById('app__container').scrollTop = 0
    next()
  },
  computed: {
    clients () {
      return this.$store.state.clients.filtered
    },
    disclaimer () {
      const category = get(this.$route.query, 'category', null)
      return category === null ? this.$t('list.mainDisclaimer') : this.$t('list.otherDisclaimer')
    },
    countries () {
      const isos = sortedUniq(sortBy(compact(flatten(map(this.$store.state.clients.all, client => map(split(client.country_iso_3, ','), iso => trim(iso)))))))
      const countries = map(isos, iso => {
        return { iso, label: this.translateIsoCountry(iso) }
      })
      countries.unshift({ iso: '', label: this.$t('global.all') })
      return countries
    },
    selectedCountry () {
      const country = get(this.$route.query, 'country', '')
      return country === '' ? this.$t('list.country') : this.translateIsoCountry(country)
    },
    banks () {
      const banks = sortedUniq(map(sortBy(this.$store.state.clients.all, 'bank_filing_sars___gallery'), 'bank_filing_sars___gallery'))
      banks.unshift(this.$t('global.all'))
      return banks
    },
    selectedBank () {
      const bank = get(this.$route.query, 'bank', '')
      return bank === '' ? this.$t('list.banksFilingSars') : bank
    }
  },
  methods: {
    kebabCase,
    selectCountry (country) {
      this.$router.push({ name: this.$route.name, params: this.$route.params, query: { ...this.$route.query, country } })
    },
    selectBank (bank) {
      if (bank === this.$t('global.all')) bank = ''
      this.$router.push({ name: this.$route.name, params: this.$route.params, query: { ...this.$route.query, bank } })
    }
  }
}
</script>

<style lang="scss">
  .list-clients {
    background: var(--app-bg);
    border-radius: 0 8px 8px 8px;
    color: var(--app-color);
    min-height: 100%;

    &__filters {
      &__title {
        font-size: 0.9rem;
        vertical-align: baseline;
      }

      &__dropdown {
        .dropdown-toggle {
          background-color: #000;
          color: white;
          max-width: 100%;
          padding: 0 4px;
          width: 166px;
        }

        &--default .dropdown-toggle {
          background-color: #808080;
        }

        .dropdown-menu {
          max-height: 50vh;
          overflow-y: auto;
        }
      }
    }

    &__client {
      &__image {
        position: relative;

        &:after {
          -webkit-transition: all 0.5s;
          background: rgba(0, 0, 0, 0.6);
          content: '\A';
          height: 100%;
          left: 0;
          opacity: 0;
          position: absolute;
          top: 0;
          transition: all 0.5s;
          width: 100%;
        }
      }

      a {
        color: black;
        text-decoration: none;
      }

      &__icon {
        color: white;
        font-size: 70px;
        left: 50%;
        opacity: 0;
        position: absolute;
        text-align: center;
        top: 35%;
        transform: translate(-50%, -50%);
        -ms-transform: translate(-50%, -50%);
      }

      &:hover &__image:after,
      &:hover &__icon {
        opacity: 1;
      }
    }

    @media (min-width: 768px) {
      .list-clients__filters__dropdown__text {
        text-align: left;
      }
    }
  }
</style>
